import {
  Account,
  AppField,
  Application,
  BaseRecord,
  GrayColors,
  LinkedRecordFieldParams,
  Member,
  PermissionAccess,
  Preferences,
  RecordTerm,
  Solution,
  Team,
  WrappedRecord,
} from "@smartsuite/types";
import { gridViewScrollOptions } from "@smartsuite/gridview/lib/components/GridView/GridView.constants";
import { GridScrollOptions } from "@smartsuite/grid/lib/src/types";
import { SelectedCell } from "@smartsuite/gridview";
import { Icon, Text } from "@smartsuite/react-ui/lib";
import cx from "classnames";
import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { noop } from "rxjs";
import {
  GridViewProps,
  GridViewGridRefInstance,
} from "@smartsuite/gridview/lib/components/GridView/GridView.types";
import { defaultCanGridViewColumnDrop } from "@smartsuite/gridview/lib/components/GridView/GridView.helpers";
import { getFieldSlug } from "@smartsuite/fields-logic/lib/utils/fields";

import { useFormConfig } from "../../../../../../hooks/useFormConfig";
import { useFormContext } from "../../../../../../hooks/useFormContext";
import { UseFormFieldResult } from "../../../../../../hooks/useFormField";
import {
  FormConfigApplicationStructureItem,
  FormConfigItem,
  LinkedRecordObject,
} from "../../../../../../types/form";
import { $t } from "../../../../../../utils/intl";
import { UseLinkedRecordsResult } from "../../hooks/useLinkedRecords";
import { useLinkedRecordRelatedRecords } from "../../hooks/useLinkedRecordRelatedRecords";
import { getTableVisibleFields } from "../../linked-records.helpers";
import { formsLinkedRecordGridText } from "./formsLinkedRecordGrid.text";
import {
  createGridViewFieldRenderer,
  fieldParametersFormRenderer,
  gridViewAggregationRenderer,
  gridViewGroupRenderer,
} from "./gridViewRenderers";
import { GridViewWithLoader } from "./GridViewWithLoader";
import { LinkedRecordTableRowActions } from "./LinkedRecordTableRowActions/LinkedRecordTableRowActions";
import {
  formStubAccountPlan,
  mapFormConfigFieldToAppField,
  setPrimaryAsFirstField,
} from "./linkedRecordGrid.helpers";
import { TableAddRow } from "./TableAddRow/TableAddRow";
import { useLinkedRecordTableFieldActions } from "./hooks/useLinkedRecordTableFieldActions";
import { useLinkedRecordTableEdit } from "./hooks/useLinkedRecordTableEdit";
import { useLinkedRecordTableRecordActions } from "./hooks/useLinkedRecordTableRecordActions";

import "./LinkedRecordTable.sass";

const baseScrollOptions: GridScrollOptions = {
  ...gridViewScrollOptions,
  headHeight: 42,
  footerHeight: 0,
  extraHeight: 54,
  extraRightWidth: 172,
  extraLeftWidth: 50,
  bufferHorizonal: 75,
  gutterLeft: 0,
};

const FORM_RECORD_TERM: RecordTerm = "record";

export interface LinkedRecordTableProps {
  field: FormConfigApplicationStructureItem;
  formItem: FormConfigItem;
  formField: UseFormFieldResult<LinkedRecordObject[]>;
  linkedRecords: UseLinkedRecordsResult;
  readOnly?: boolean;
  isMultiple: boolean;
  onLinkRecordClick: () => void;
  onChangeFocused?: (isFocused: boolean) => void;
  onRowClick?: (recordId: string) => void;
}

const buildStubSolution = (label: string): Solution =>
  ({
    id: "form-solution",
    name: label,
    slug: "form-solution",
    logo_color: { color: "#000000" },
    logo_icon: "folder",
    description: "",
    permissions: {} as Solution["permissions"],
    status: "active",
    records_count: 0,
    members_count: 0,
  } as unknown as Solution);

const buildStubMember = (preferences: Preferences): Member =>
  ({
    id: "form-member",
    first_name: "",
    last_name: "",
    preferences,
  } as unknown as Member);

export const LinkedRecordTable: FunctionComponent<LinkedRecordTableProps> = ({
  field,
  formItem,
  formField,
  linkedRecords,
  readOnly = false,
  isMultiple,
  onLinkRecordClick,
  onChangeFocused = noop,
  onRowClick,
}) => {
  const formContext = useFormContext();
  const gridRef = useRef<GridViewGridRefInstance>({} as GridViewGridRefInstance);

  const linkedApplicationFields = linkedRecords.linkedApplicationFields;

  const tableVisibleFields = useMemo(
    () => getTableVisibleFields(formItem, linkedApplicationFields),
    [formItem, linkedApplicationFields]
  );

  // sort_data is cleared so the form's table preserves user insertion order. Any sort
  // configured on the linked-record field upstream is irrelevant to the form-fill flow,
  // where rows must append after existing ones in the order they were added.
  const [linkedRecordField, setLinkedRecordField] = useState<AppField<LinkedRecordFieldParams>>(
    () => {
      const base = mapFormConfigFieldToAppField(field);
      return {
        ...base,
        params: { ...base.params, visible_fields: tableVisibleFields, sort_data: [] },
      } as AppField<LinkedRecordFieldParams>;
    }
  );

  useEffect(() => {
    const base = mapFormConfigFieldToAppField(field);
    setLinkedRecordField({
      ...base,
      params: { ...base.params, visible_fields: tableVisibleFields, sort_data: [] },
    } as AppField<LinkedRecordFieldParams>);
  }, [field, tableVisibleFields]);

  const structure = useMemo(() => {
    const raw = tableVisibleFields
      .map((slug) => linkedApplicationFields?.[slug])
      .filter((item): item is AppField => !!item);
    return setPrimaryAsFirstField(raw);
  }, [linkedApplicationFields, tableVisibleFields]);

  const application = useMemo((): Application => {
    return {
      id: field.params?.linked_application ?? "linked-application",
      name: field.label,
      slug: "linked-application",
      structure,
      solution: "form-solution",
      first_created: { by: "form", on: new Date().toISOString() },
    } as Application;
  }, [field.label, field.params?.linked_application, structure]);

  const solution = useMemo(() => buildStubSolution(field.label), [field.label]);

  const userPreferences = formContext.preferences;
  const memberAccess: PermissionAccess = "owner";
  const currentMember = useMemo(() => buildStubMember(userPreferences), [userPreferences]);
  const activeTeams: Team[] = [];
  const account: Account | undefined = undefined;

  // Preserve user insertion order (newly added records append after existing rows) instead
  // of inheriting the API response order, which is sorted by title.
  const applicationRecords = useMemo((): BaseRecord[] => {
    const all = linkedRecords.records ?? [];
    if (!all.length || !Array.isArray(formField.value)) return [];
    const recordsById = new Map(all.map((record) => [String(record.id), record]));
    return formField.value
      .map((linkedRecord) => recordsById.get(String(linkedRecord?.id)))
      .filter((record): record is NonNullable<typeof record> => !!record) as BaseRecord[];
  }, [linkedRecords.records, formField.value]);

  const onRecordRemove = useCallback(
    (recordId: string) => {
      formField.onChange(
        (formField.value ?? []).filter((linkedRecord) => linkedRecord.id !== recordId)
      );
    },
    [formField]
  );

  const changeRecordsOrdering = useCallback((_records: BaseRecord[]) => {
    return undefined;
  }, []);

  const onCreateRecord = useCallback(noop, []);

  const onChangeLinkedRecordField = useCallback((next: typeof linkedRecordField) => {
    setLinkedRecordField(next);
  }, []);

  const {
    settings,
    structure: gridStructure,
    visibleFields,
    records,
    columnWidths,
    onColumnWidthChange,
  } = useLinkedRecordTableEdit({
    isReadOnly: true,
    currentMember,
    applicationRecords,
    application,
    linkedRecordField,
    memberAccess,
    userPreferences,
    activeTeams,
    recordTerm: FORM_RECORD_TERM,
    limitByHeight: false,
    onChangeLinkedRecordField,
    onShowAll: undefined,
  });

  const formConfig = useFormConfig();

  const fieldsToShow = useMemo(
    () => Object.fromEntries(gridStructure.map((f) => [getFieldSlug(f), f])),
    [gridStructure]
  );

  const { data: relatedRecords } = useLinkedRecordRelatedRecords({ field, visibleFields });

  const fieldRenderer = useMemo(
    () =>
      createGridViewFieldRenderer({
        fieldsToShow,
        tags: formConfig?.tags,
        relatedRecords,
        userPreferences,
      }),
    [fieldsToShow, formConfig?.tags, relatedRecords, userPreferences]
  );

  const {
    isRecordsLimitExceeded,
    getAvailableRecordsToCreate,
    onOpenRecordsLimitExceededModal,
    onOpenFieldListingModal,
    openExpandViewModal,
    confirmBulkPaste,
    confirmCellsDelete,
    onSaveRecord,
    onDeleteRecord,
    onUnlinkRecord,
    onBulkPaste,
    onBulkUpdateFieldValue,
    onCopyRecordLink,
    onPrintRecord,
    onExportRecordToPdf,
    onExportRecordToExcel,
    onExportRecordToCsv,
    onExportRecordToGoogleSheets,
    onShareRecordByEmail,
    onReorderRecords,
  } = useLinkedRecordTableRecordActions({
    account,
    application,
    linkedRecordField,
    currentRecord: { id: "" } as BaseRecord,
    solution,
    applicationRecords,
    userPreferences,
    currentMember,
    showAllOnRecordsSave: false,
    changeRecordsOrdering,
    onCreateRecord,
    onRecordRemove,
    onShowAll: undefined,
  });

  const {
    onChangeField,
    onDeleteField,
    onDuplicateField,
    onFieldInfoIconClick,
    onOpenFieldFormModal,
    onEditFieldPermissions,
  } = useLinkedRecordTableFieldActions({
    linkedRecordField,
    application,
    solution,
    memberAccess,
    visibleFields,
    onChangeLinkedRecordField,
  });

  // GridView: useEffect(..., [onCellSelected, selectedCell]). formField.onChangeFocused changes
  // identity when focus state updates; a stable callback avoids re-firing the effect in a loop.
  const onChangeFocusedRef = useRef(onChangeFocused);
  onChangeFocusedRef.current = onChangeFocused;

  // GridView fires onCellSelected(null) on mount (selectedCell is initially null). Forwarding
  // that as a blur would mark the field touched before any user interaction and immediately
  // surface required-validation errors. Only forward real focus transitions.
  const wasFocusedRef = useRef(false);

  const handleSelectedCellChange = useCallback((cell: SelectedCell<WrappedRecord> | null) => {
    const nextFocused = !!cell;
    if (nextFocused === wasFocusedRef.current) return;
    wasFocusedRef.current = nextFocused;
    onChangeFocusedRef.current(nextFocused);
  }, []);

  const classes = cx("table-linked-record--edit");

  const gridViewProps: GridViewProps = {
    className: classes,
    settings,
    solution,
    application,
    structure: gridStructure,
    records,
    applicationRecords,
    memberAccess,
    member: currentMember,
    visibleFields,
    fixedColumnsCount: 0,
    sortData: [],
    groupByData: [],
    collapsedGroups: [],
    maxGroupsCount: 0,
    filterData: undefined,
    userPreferences,
    accountPlan: formStubAccountPlan,
    columnWidths,
    externalViewportInfo: undefined,
    canEditField: undefined,
    canDeleteRecord: () => true,
    fieldRenderer,
    groupFieldRenderer: gridViewGroupRenderer,
    aggregationRenderer: gridViewAggregationRenderer,
    fieldFormRenderer: fieldParametersFormRenderer,
    onSaveRecord,
    onDeleteRecord,
    onCopyRecordLink,
    onOpenRecordModal: undefined,
    onReorder: onReorderRecords,
    onBulkPaste,
    onBulkUpdateFieldValue,
    onPrintRecord,
    onExportRecordToPdf,
    onExportRecordToExcel,
    onExportRecordToCsv,
    onExportRecordToGoogleSheets,
    onShareRecordByEmail,
    onChangeField,
    onDeleteField,
    onDuplicateField,
    onFieldInfoIconClick,
    onEditFieldPermissions,
    onOpenFieldFormModal,
    onOpenRecordsLimitExceededModal,
    onOpenFieldListingModal,
    openExpandViewModal,
    confirmBulkPaste,
    confirmCellsDelete,
    canGridViewColumnDrop: defaultCanGridViewColumnDrop,
    isRecordsLimitExceeded,
    getAvailableRecordsToCreate,
    onColumnWidthChange,
    onCellSelected: handleSelectedCellChange,
  };

  const scrollOptions = useMemo(
    () => ({
      ...baseScrollOptions,
      disableVerticalScroll: false,
    }),
    []
  );

  const hasRecords = records.length > 0;

  // In single-entry mode the button must remain visible even when a record is already
  // linked: clicking it opens the modal (which excludes the linked record from results)
  // and selecting a different record replaces the existing link.
  const isLinkToButtonVisible = (!settings.canAddRecord || hasRecords) && !readOnly;

  const linkedRecordButtonText = useMemo(
    () =>
      $t(formsLinkedRecordGridText.linkToTable, {
        table: field.label,
      }),
    [field.label]
  );

  // Distinguishes "no access to linked Table" from "admin marked the field read-only".
  // The backend (server/apps/forms/api/v1/views.py:_get_read_only_field_slugs) sets
  // params.read_only on a LR item for two reasons: either the member can't write to the
  // field, OR the member can't access the linked application at all. Only the second
  // case must surface a no-access message — and it can be detected because the linked
  // application fields response is filtered down to the member's readable slugs and
  // returns an empty object when no fields are readable.
  const hasReadableLinkedFields =
    !!linkedApplicationFields && Object.keys(linkedApplicationFields).length > 0;
  const isLinkedTableInaccessible = readOnly && !hasReadableLinkedFields;

  if (isLinkedTableInaccessible) {
    return (
      <div className="linked-record-table-grid-host linked-record-table-grid-host--no-access">
        <Icon name="lock" size="S" color={GrayColors["G-4"]} />
        <Text size="l" color="secondary">
          {$t(formsLinkedRecordGridText.noAccessToLinkedTable)}
        </Text>
      </div>
    );
  }

  return (
    <div className="linked-record-table-grid-host">
      <GridViewWithLoader ref={gridRef} {...gridViewProps} scrollOptions={scrollOptions}>
        <LinkedRecordTableRowActions
          canUnlinkRecord={true}
          gridRef={gridRef}
          onUnlink={onUnlinkRecord}
        />
      </GridViewWithLoader>
      {isLinkToButtonVisible && (
        <TableAddRow title={linkedRecordButtonText} onClick={onLinkRecordClick} />
      )}
    </div>
  );
};
